import "./ChangeRequestsPage.css";

import { ChangesetDTO, ChangesetStates, SpacePageDTO } from "../models/ChangeRequestDTO";
import { Card, Chip, DataTable, Field, Page, Panel, Select, TwoLine } from "../../../ds";
import type { Column, SelectOption } from "../../../ds";
import { ChangeRequestDetail, authorLabel, shortPerson } from "../components/ChangeRequestDetail";
import { ChangeRequestStateChip, stateLabel } from "../components/ReviewKindBadge";
import { IChangeRequestService, changeRequestService, dispatchChangeRequestsChanged, errorMessage } from "../services/ChangeRequestService";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { DateTimeHelper } from "../../../lib/utils/DateTimeHelper";
import { ILockService } from "../../locks/services/LockService";
import { PushPolicySelector } from "../components/PushPolicySelector";
import { UISContext } from "../../../lib/infrastructure/ui/UIServices";
import { useSpaceLocks } from "../../locks/hooks/useSpaceLocks";

const stateOptions: SelectOption[] = ["proposed", "approved", "applied", "rejected", "withdrawn", "failed", "all"]
    .map((s) => ({ value: s, label: s === "all" ? "All" : stateLabel(s) }));
const PageSize = 200;

interface IChangeRequestsPageProps {
    /** Injected in tests; the shared facade client otherwise. */
    service?: IChangeRequestService;
    /** Injected in tests; the shared lock client otherwise (lock badges on the changed pages). */
    lockService?: ILockService;
}

const plural = (n: number, one: string, many: string) => (n === 1 ? `1 ${one}` : `${n} ${many}`);

/** "via orbital-cli · revision 2" under a change request's message. */
function origin(cs: ChangesetDTO): string {
    return [cs.client ? `via ${cs.client}` : "", cs.revision ? `revision ${cs.revision}` : ""].filter(Boolean).join(" · ");
}

/**
 * Change requests of a space (`/spaces/:spaceId/change-requests`): changesets parked by the `propose` push policy or
 * sent with `orbital ark propose`, reviewed here (approve / reject with a reason / withdraw by the author).
 * `?cr=<id>` opens one for review and `?page=<key>` picks the changed page shown, so a review can be linked to.
 */
export function ChangeRequestsPage(props: IChangeRequestsPageProps) {
    const { spaceId } = useParams();
    const service = props.service ?? changeRequestService();
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedId = searchParams.get("cr") ?? undefined;
    const selectedItemKey = searchParams.get("page") ?? undefined;

    const [state, setState] = useState("proposed");
    const [items, setItems] = useState<ChangesetDTO[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const [pages, setPages] = useState<SpacePageDTO[]>([]);
    const [currentUserId, setCurrentUserId] = useState<string | undefined>();
    // display names of the authors in the list, from the directory (ids the directory does not know stay short ids)
    const [names, setNames] = useState<Record<string, string>>({});
    const { marks: lockMarks } = useSpaceLocks(spaceId, props.lockService);
    // the space's name as the space module loaded it (header, tab title); absent outside the app's providers
    const selectedSpace = useContext(UISContext)?.selectedSpace;
    const spaceName = selectedSpace && selectedSpace.id === spaceId ? selectedSpace.name : undefined;

    const load = useCallback(() => {
        if (!spaceId) return;
        setIsLoading(true);
        setError(undefined);
        service
            .list(spaceId, state, PageSize)
            .then((res) => {
                setItems(res);
                setIsLoading(false);
            })
            .catch((err) => {
                setError(errorMessage(err));
                setIsLoading(false);
            });
    }, [service, spaceId, state]);

    const loadPages = useCallback(() => {
        if (!spaceId) return;
        service.getPages(spaceId).then(setPages).catch(() => { /* changes fall back to ids, no stale check */ });
    }, [service, spaceId]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (!spaceId) return;
        let alive = true;
        loadPages();
        service.getCurrentUserId().then((id) => alive && setCurrentUserId(id)).catch(() => { /* no withdraw button */ });
        return () => {
            alive = false;
        };
    }, [service, spaceId, loadPages]);

    const authorIds = useMemo(
        () => Array.from(new Set(items.map((cs) => cs.authorId).filter((id): id is string => !!id && id !== currentUserId))).sort().join(","),
        [items, currentUserId]
    );
    useEffect(() => {
        if (!authorIds || selectedId) return;
        let alive = true;
        service
            .getUserNames(authorIds.split(","))
            .then((found) => alive && setNames((prev) => ({ ...prev, ...Object.fromEntries(found.map((u) => [u.id, u.displayName])) })))
            .catch(() => { /* ids stay ids */ });
        return () => {
            alive = false;
        };
    }, [service, authorIds, selectedId]);

    const handleChanged = useCallback(() => {
        load();
        loadPages();
        dispatchChangeRequestsChanged();
    }, [load, loadPages]);

    const open = useCallback((id: string) => setSearchParams({ cr: id }), [setSearchParams]);
    const close = useCallback(() => setSearchParams({}), [setSearchParams]);
    const selectItem = useCallback((key: string) => {
        if (selectedId) setSearchParams({ cr: selectedId, page: key }, { replace: true });
    }, [selectedId, setSearchParams]);

    const author = useCallback((cs: ChangesetDTO) => {
        const label = authorLabel(cs.authorId, currentUserId);
        return label === "you" || label === "unknown" ? label : names[label] ?? shortPerson(label);
    }, [currentUserId, names]);

    const columns = useMemo((): Column<ChangesetDTO>[] => [
        {
            key: "message",
            header: "Change request",
            render: (cs) => (
                <TwoLine mono={false}
                    primary={cs.message ? <span className="cr-list-message">{cs.message}</span> : <span className="cr-list-message is-empty">(no message)</span>}
                    secondary={origin(cs)} />
            ),
        },
        { key: "author", header: "Author", width: "12rem", render: (cs) => <span className="cr-nowrap" title={cs.authorId ?? undefined}>{author(cs)}</span> },
        {
            key: "created",
            header: "Created",
            width: "9rem",
            render: (cs) => <span className="cr-nowrap" title={DateTimeHelper.formatDateTime(cs.createdAt)}>{DateTimeHelper.formatDateTimeAsTimeAgo(cs.createdAt)}</span>,
        },
        { key: "changes", header: "Changes", width: "7rem", numeric: true, render: (cs) => cs.opCount },
        { key: "state", header: "State", width: "9rem", render: (cs) => <ChangeRequestStateChip state={cs.state} /> },
    ], [author]);

    if (selectedId) {
        return (
            <Page header={false} width="full" className="cr-page is-review">
                <ChangeRequestDetail
                    key={selectedId}
                    changesetId={selectedId}
                    service={service}
                    spaceId={spaceId}
                    pages={pages}
                    locks={lockMarks}
                    currentUserId={currentUserId}
                    selectedItemKey={selectedItemKey}
                    onSelectItem={selectItem}
                    onChanged={handleChanged}
                    onClose={close}
                    spaceName={spaceName}
                />
            </Page>
        );
    }

    // how many wait for a review, when the list loaded says so (the proposed ones, or all of them)
    const waiting = state === ChangesetStates.Proposed || state === "all" ? items.filter((cs) => cs.state === ChangesetStates.Proposed).length : undefined;
    const emptyText = `No change requests ${state === "all" ? "in this space" : `in state "${stateLabel(state).toLowerCase()}"`}.`;

    return (
        <Page
            className="cr-page"
            title="Change requests"
            tabTitle={["Change requests", spaceName]}
            eyebrow={spaceName ? `Space · ${spaceName}` : undefined}
            status={waiting ? <Chip tone="warning" dot size="sm">{`${waiting} proposed`}</Chip> : undefined}
            intro="Pushes from the orbital CLI and Ark Studio wait here while the push policy is propose. Open one to review its diff, settle conflicts, and approve or reject it."
            actions={spaceId ? <PushPolicySelector spaceId={spaceId} service={service} /> : undefined}
        >
            <Panel
                title=""
                aria-label="Change requests"
                flush
                toolbar={
                    <div className="cr-list-toolbar">
                        <div className="cr-list-filter">
                            <Field label="State">
                                <Select options={stateOptions} value={state} onChange={(value) => value && setState(value)} />
                            </Field>
                        </div>
                        {!isLoading && !error ? (
                            <span className="cr-list-count">
                                {plural(items.length, "change request", "change requests")}
                                {waiting !== undefined ? ` · ${waiting} waiting for review` : ""}
                            </span>
                        ) : null}
                    </div>
                }
                footer={items.length && !error ? <span>Showing {plural(items.length, "change request", "change requests")} · newest first</span> : undefined}
            >
                <DataTable<ChangesetDTO>
                    columns={columns}
                    items={items}
                    rowKey={(cs) => cs.id}
                    isLoading={isLoading && items.length === 0}
                    error={error ? new Error(error) : undefined}
                    onRetry={load}
                    emptyTitle="No change requests"
                    emptyText={emptyText}
                    caption="Change requests"
                    onRowClick={(cs) => open(cs.id)}
                    mobileCard={(cs) => (
                        <Card
                            title={cs.message || "(no message)"}
                            letter=""
                            headingLevel={2}
                            chip={<ChangeRequestStateChip state={cs.state} />}
                            description={`${author(cs)} · ${DateTimeHelper.formatDateTimeAsTimeAgo(cs.createdAt)} · ${plural(cs.opCount, "change", "changes")}`}
                            footer={origin(cs) || undefined}
                            onClick={() => open(cs.id)}
                        />
                    )}
                />
            </Panel>
        </Page>
    );
}
